/**
 * ShiftSummary — top "⚡ Shift Summary" card on the Home page.
 *
 * Two regions:
 *   - HEADER (dark blue gradient) — date, shift name, time range, weather
 *   - BODY   (surface bg) — 7 colored stat tiles, then 3 info boxes
 *
 * Reads everything it needs from the desktop store. No props.
 *
 * Weather is auto-fetched on mount and every 10 minutes from
 * /api/v1/weather (Open-Meteo via the Worker, 5-minute server-side cache).
 * If the fetch fails or location isn't configured, falls back to whatever
 * is already in the store (which can still be set manually elsewhere).
 */
import { useEffect } from 'react'
import { useDesktopStore } from '../../stores/desktop'
import { workerGetV1 } from '../../api/worker'
import { getShiftTime } from './shiftUtils'

interface WeatherResponse {
  temp?: number
  conditions?: string
  wind?: number
  city?: string
  error?: string
}

interface StatTile {
  num: number
  label: string
  color: string
  bg: string
  border: string
}

export default function ShiftSummary() {
  const {
    personnel, securityKeys, offRadios, supRadios, recorders,
    oosIds, deskOfficer, currentDate, shift, weather, setWeather,
  } = useDesktopStore()

  // Auto-fetch weather from Worker on mount + every 10 minutes.
  // Worker caches upstream Open-Meteo for 5 min, so this is cheap even
  // with multiple sessions open. Manual edits via setWeather still work —
  // they just get overwritten on the next 10-minute refresh, which is
  // the desired behavior for an auto-updating field.
  useEffect(() => {
    let cancelled = false

    const fetchWeather = async () => {
      try {
        const data = await workerGetV1<WeatherResponse>('/api/v1/weather')
        if (cancelled) return
        if (data.error) return  // tenant location not configured — leave existing value alone
        if (typeof data.temp === 'number' && data.conditions) {
          setWeather({ temp: `${data.temp}\u00b0F`, conditions: data.conditions })
        }
      } catch {
        // Network/auth failure — silent. Keep whatever's already in the store.
      }
    }

    fetchWeather()
    const id = setInterval(fetchWeather, 10 * 60 * 1000)
    return () => { cancelled = true; clearInterval(id) }
  }, [setWeather])

  const onDuty       = personnel.filter(p => p.name).length
  const keysOut      = securityKeys.filter(k => k.assignedTo).length
  const keysAvail    = securityKeys.filter(k => k.status === 'good' && !k.assignedTo).length
  const radiosOut    = [...offRadios, ...supRadios].filter(r => r.assignedTo).length
  const recsAssigned = recorders.filter(r => r.assignedTo && !r.oos).length
  const badgesOOS    = oosIds.length
  const totalOOS     = recorders.filter(r => r.oos).length + badgesOOS

  const shiftTime = getShiftTime(shift)
  const supNames  = personnel.slice(0, 3).filter(p => p.name).map(p => {
    const s = p.name.replace(/\s*\(.*?\)\s*/g, '').trim()
    return s.includes(',') ? s.split(',')[0].trim() : s
  })
  const dateStr = currentDate
    ? new Date(currentDate + 'T12:00:00').toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : 'No date set'

  const stats: StatTile[] = [
    { num: onDuty,       label: 'On Duty',    color: '#4ade80', bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.3)' },
    { num: keysOut,      label: 'Keys Out',   color: '#fbbf24', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' },
    { num: keysAvail,    label: 'Keys Avail', color: '#60a5fa', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.3)' },
    { num: radiosOut,    label: 'Radios Out', color: '#a78bfa', bg: 'rgba(139,92,246,0.12)', border: 'rgba(139,92,246,0.3)' },
    { num: recsAssigned, label: 'Recorders',  color: '#f472b6', bg: 'rgba(236,72,153,0.12)', border: 'rgba(236,72,153,0.3)' },
    { num: badgesOOS,    label: 'Badges OOS', color: '#fb923c', bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.3)' },
    { num: totalOOS,     label: 'Total OOS',  color: '#f87171', bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.3)' },
  ]

  const infoBox: React.CSSProperties = {
    background: 'var(--bg-deep)',
    borderRadius: 10,
    padding: '10px 12px',
  }

  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)' }}>
      {/* Header — gradient */}
      <div
        style={{
          background: 'linear-gradient(135deg,#0a1830 0%,#1e40af 100%)',
          padding: '16px 20px',
          color: '#fff',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>⚡ Shift Summary</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>
              {dateStr} • {shift} Shift
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{shiftTime}</div>
            {weather.temp && (
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{weather.temp}</div>
            )}
            {weather.conditions && (
              <div style={{ fontSize: 12, opacity: 0.85 }}>{weather.conditions}</div>
            )}
          </div>
        </div>
      </div>

      {/* Body — stat tiles + info boxes */}
      <div style={{ background: 'var(--bg-surface)', padding: '16px 20px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7,1fr)',
            gap: 12,
            marginBottom: 16,
          }}
        >
          {stats.map((s, i) => (
            <div
              key={i}
              style={{
                background: s.bg,
                borderRadius: 12,
                padding: '14px 10px',
                textAlign: 'center',
                border: `2px solid ${s.border}`,
              }}
            >
              <div style={{ fontSize: 28, fontWeight: 800, color: s.color, lineHeight: 1 }}>
                {s.num}
              </div>
              <div style={{ fontSize: 11, color: '#d0d8e8', fontWeight: 700, marginTop: 4 }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div style={infoBox}>
            <div
              style={{
                fontSize: 10,
                color: 'var(--text-muted)',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              Supervisor(s)
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--text-primary)',
                marginTop: 3,
              }}
            >
              {supNames.length ? supNames.join(' / ') : '—'}
            </div>
          </div>
          <div style={infoBox}>
            <div
              style={{
                fontSize: 10,
                color: 'var(--text-muted)',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              Desk Officer
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--text-primary)',
                marginTop: 3,
              }}
            >
              {deskOfficer || '—'}
            </div>
          </div>
          <div style={infoBox}>
            <div
              style={{
                fontSize: 10,
                color: 'var(--text-muted)',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              Shift Time
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--text-primary)',
                marginTop: 3,
              }}
            >
              {shiftTime}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
